import { Button, Select } from '@chopchop-se/ui';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { StockByArticleRow, StockRow } from '@warehouse/shared';
import { ArrowLeftRight } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { warehouseApi, warehouseKeys } from '../api';
import { ExpiryCell } from '../components/ExpiryCell';
import { DataTable, type DataTableColumn } from '../../../components/DataTable';
import { PageHeader } from '../../../components/PageHeader';
import { useListState } from '../../../hooks/useListState';
import { formatDateTime, formatQty } from '../../../lib/format';
import { listQueryKey } from '../../../lib/list-query';
import { useLiveQuery } from '../../../realtime/useLiveQuery';

type Mode = 'quantum' | 'article';

/**
 * Stock views — the "where is it" half of traceability. Two projections of the
 * same data: per quantum (article + lot + pallet + bin, what the ledger keys
 * on) and rolled up per article.
 */
export function StockPage(): ReactNode {
  const { t } = useTranslation(['warehouseCore', 'common']);
  const [mode, setMode] = useState<Mode>('quantum');

  return (
    <>
      <PageHeader
        title={t('warehouseCore:stock.title')}
        subtitle={t('warehouseCore:stock.subtitle')}
        actions={
          <Link to="/warehouse/movements">
            <Button variant="secondary" size="sm" icon={<ArrowLeftRight />}>
              {t('warehouseCore:stock.showMovements')}
            </Button>
          </Link>
        }
      />

      <div className="mb-4 inline-flex rounded-full border border-surface-border p-1">
        {(['quantum', 'article'] as const).map((value) => (
          <button
            key={value}
            type="button"
            onClick={() => {
              setMode(value);
            }}
            className={`rounded-full px-4 py-1.5 text-sm transition-colors ${
              mode === value ? 'bg-brand-600 text-white' : 'text-ink-muted hover:text-ink'
            }`}
          >
            {t(value === 'quantum' ? 'warehouseCore:stock.byQuantum' : 'warehouseCore:stock.byArticle')}
          </button>
        ))}
      </div>

      {mode === 'quantum' ? <QuantumTable /> : <ArticleTable />}
    </>
  );
}

function QuantumTable(): ReactNode {
  const { t } = useTranslation(['warehouseCore', 'common']);
  const { state, setState, setFilter } = useListState({ defaultSort: 'binCode', defaultDir: 'asc' });

  const { data, isFetching, error } = useQuery({
    queryKey: warehouseKeys.stock(listQueryKey(state)),
    queryFn: () => warehouseApi.listStock(state),
    placeholderData: keepPreviousData,
  });
  useLiveQuery(['stock'], warehouseKeys.stock());

  const { data: areas } = useQuery({
    queryKey: warehouseKeys.areas(),
    queryFn: () => warehouseApi.listAreas(),
  });

  const columns: DataTableColumn<StockRow>[] = [
    {
      id: 'bin',
      header: t('warehouseCore:stock.bin'),
      sortField: 'binCode',
      cell: (row) => <span className="font-mono text-sm">{row.binCode}</span>,
    },
    {
      id: 'article',
      header: t('warehouseCore:stock.article'),
      sortField: 'articleNo',
      cell: (row) => (
        <div className="min-w-0">
          <p className="truncate text-sm text-ink">{row.articleName}</p>
          <p className="font-mono text-xs text-ink-muted">{row.articleNo}</p>
        </div>
      ),
    },
    {
      id: 'lot',
      header: t('warehouseCore:stock.lot'),
      sortField: 'lotNo',
      cell: (row) =>
        row.lotId ? (
          <Link to={`/warehouse/lots/${row.lotId}`} className="font-mono text-sm text-brand-600 hover:underline">
            {row.lotNo}
          </Link>
        ) : (
          '—'
        ),
      hideBelow: 'md',
    },
    {
      id: 'bestBefore',
      header: t('warehouseCore:stock.bestBefore'),
      sortField: 'bestBefore',
      cell: (row) => row.bestBefore ?? '—',
      hideBelow: 'lg',
    },
    {
      id: 'pallet',
      header: t('warehouseCore:stock.pallet'),
      sortField: 'lpn',
      cell: (row) =>
        row.palletId ? (
          <Link
            to={`/warehouse/pallets/${row.palletId}`}
            className="font-mono text-sm text-brand-600 hover:underline"
          >
            {row.lpn}
          </Link>
        ) : (
          <span className="text-xs text-ink-muted">{t('warehouseCore:stock.loose')}</span>
        ),
      hideBelow: 'md',
    },
    {
      id: 'qty',
      header: t('warehouseCore:stock.quantity'),
      align: 'right',
      sortField: 'qtyBase',
      cell: (row) => (
        <span className="font-mono">{formatQty(row.qtyBase, { unit: row.unitCode ?? undefined })}</span>
      ),
    },
    {
      id: 'updated',
      header: t('warehouseCore:stock.updated'),
      sortField: 'updatedAt',
      cell: (row) => formatDateTime(row.updatedAt),
      hideBelow: 'xl',
    },
  ];

  return (
    <DataTable
      columns={columns}
      rows={data?.items ?? []}
      rowKey={(row) => row.id}
      state={state}
      onStateChange={setState}
      total={data?.total ?? null}
      loading={isFetching}
      error={error}
      searchPlaceholder={t('warehouseCore:stock.search')}
      emptyTitle={t('warehouseCore:stock.empty')}
      emptyDescription={t('warehouseCore:stock.emptyHelp')}
      filters={
        <>
          <Select
            aria-label={t('warehouseCore:stock.area')}
            className="w-44"
            value={String(state.filters?.areaId ?? '')}
            options={[
              { value: '', label: t('warehouseCore:common.all') },
              ...(areas ?? []).map((a) => ({ value: a.id, label: `${a.code} · ${a.name}` })),
            ]}
            onChange={(event) => {
              setFilter('areaId', event.target.value || undefined);
            }}
          />
          <Select
            aria-label={t('warehouseCore:stock.onPallet')}
            className="w-40"
            value={
              state.filters?.hasPallet === true ? 'pallet' : state.filters?.hasPallet === false ? 'loose' : ''
            }
            options={[
              { value: '', label: t('warehouseCore:common.all') },
              { value: 'pallet', label: t('warehouseCore:stock.onPallet') },
              { value: 'loose', label: t('warehouseCore:stock.loose') },
            ]}
            onChange={(event) => {
              const v = event.target.value;
              setFilter('hasPallet', v === 'pallet' ? true : v === 'loose' ? false : undefined);
            }}
          />
          <label className="flex items-center gap-2 text-sm text-ink-muted">
            {t('warehouseCore:stock.expiresBefore')}
            <input
              type="date"
              className="rounded-lg border border-surface-border bg-surface px-2 py-1 text-sm text-ink"
              value={String(state.filters?.bestBeforeTo ?? '')}
              onChange={(event) => {
                setFilter('bestBeforeTo', event.target.value || undefined);
              }}
            />
          </label>
        </>
      }
    />
  );
}

function ArticleTable(): ReactNode {
  const { t } = useTranslation(['warehouseCore', 'common']);
  const { state, setState } = useListState({
    defaultSort: 'articleNo',
    defaultDir: 'asc',
    prefix: 'a',
  });

  const { data, isFetching, error } = useQuery({
    queryKey: warehouseKeys.stockByArticle(listQueryKey(state)),
    queryFn: () => warehouseApi.listStockByArticle(state),
    placeholderData: keepPreviousData,
  });
  useLiveQuery(['stock'], warehouseKeys.stockByArticle());

  const columns: DataTableColumn<StockByArticleRow>[] = [
    {
      id: 'article',
      header: t('warehouseCore:stock.article'),
      sortField: 'articleNo',
      cell: (row) => (
        <div className="min-w-0">
          <p className="truncate text-sm text-ink">{row.articleName}</p>
          <p className="font-mono text-xs text-ink-muted">{row.articleNo}</p>
        </div>
      ),
    },
    {
      id: 'qty',
      header: t('warehouseCore:stock.quantity'),
      align: 'right',
      sortField: 'qtyBase',
      cell: (row) => (
        <span className="font-mono text-sm">
          {formatQty(row.qtyBase, { unit: row.unitCode ?? undefined })}
        </span>
      ),
    },
    {
      id: 'bins',
      header: t('warehouseCore:stock.binCount'),
      align: 'right',
      cell: (row) => row.binCount,
      hideBelow: 'sm',
    },
    {
      id: 'lots',
      header: t('warehouseCore:stock.lotCount'),
      align: 'right',
      cell: (row) => row.lotCount,
      hideBelow: 'md',
    },
    {
      id: 'pallets',
      header: t('warehouseCore:stock.palletCount'),
      align: 'right',
      cell: (row) => row.palletCount,
      hideBelow: 'lg',
    },
    {
      id: 'bestBefore',
      header: t('warehouseCore:stock.earliestBestBefore'),
      sortField: 'earliestBestBefore',
      cell: (row) => <ExpiryCell date={row.earliestBestBefore} />,
    },
  ];

  return (
    <DataTable
      columns={columns}
      rows={data?.items ?? []}
      rowKey={(row) => row.articleId}
      state={state}
      onStateChange={setState}
      total={data?.total ?? null}
      loading={isFetching}
      error={error}
      searchPlaceholder={t('warehouseCore:stock.search')}
      emptyTitle={t('warehouseCore:stock.empty')}
      emptyDescription={t('warehouseCore:stock.emptyHelp')}
    />
  );
}
